import React from 'react';
import { View } from 'react-native';

import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { Button, Text } from '../../../components';
import { InformationCallout } from '../../../components/callout';
import { CampaignSelector } from '../../../components/selector';
import SetupSection from '../components/SetupSection';
import SetupTextField from '../components/SetupTextField';
import SetupPickerField from '../components/SetupPickerField';
import SetupSwitchField from '../components/SetupSwitchField';
import SetupPhotoSection from '../components/SetupPhotoSection';
import SetupMultiSelector from '../components/SetupMultiSelector';
import { flattenObjectDataInArray } from '../../../../common/utils/general/flattenObjectData';

import {
	CLIENT_CURRENT_VENDING_SETUP_VALUE,
	CLIENT_CURRENT_VENDING_SETUP_VALUE_NAME,
	INTERNET_CONNECTIVITY,
	INTERNET_CONNECTIVITY_NAME,
	MARKET_SETUP_MARKET_TYPE,
	MARKET_SETUP_MARKET_TYPE_NAME,
	MARKET_SETUP_MARKET_FINISH_VALUE,
	MARKET_SETUP_MARKET_FINISH_VALUE_NAME,
	MACHINE_BRAND,
	MACHINE_BRAND_NAME,
} from '../../../../common/constants';
import { TICKET_FIELDS, toNumber } from './MarketSetupDetailHelpers';
import MarketSetupBays from './MarketSetupBays';
import MarketSetupFoodOptions from './MarketSetupFoodOptions';
import { createMarketSetupDetailStyles } from './MarketSetupDetail.styles';

const MarketSetupMarketSection = ({
	marketSetup,
	isAssignedMarketSetupWorker,
	hasAssignedMarket,
	ticketDescriptions,
	updateTicketDescription,
	handleGenerateTicket,
	updateMarketSetup,
	addPhoto,
	handleUpdateMediaComment,
}) => {
	const styles = useThemedStyles(createMarketSetupDetailStyles);
	const isCustomBuildMarket = marketSetup.marketType === MARKET_SETUP_MARKET_TYPE.CUSTOM_BUILD;
	const isGondolaShelvingMarket = marketSetup.marketType === MARKET_SETUP_MARKET_TYPE.GONDOLA_SHELVING;
	const requiresMachineRemoval = marketSetup.clientCurrentVendingSetup === CLIENT_CURRENT_VENDING_SETUP_VALUE.WE_HAVE_MACHINES_AT_CLIENT_LOCATION_WCHICH_WE_WILL_REMOVE;
	const requiresModem = marketSetup.internetConnectivity === INTERNET_CONNECTIVITY.AGORA_USB_MODEM;

	return (
		<SetupSection title="Market setup">
			<InformationCallout text="If Kamil completes the site visit, record the details he provides before finishing this section." />
			<Text style={styles.sectionLabel}>Market space - site information</Text>
			<SetupTextField
				label="Confirm lift dimensions"
				value={marketSetup.liftDimensions}
				field="liftDimensions"
				onConfirm={updateMarketSetup}
				hint="Enter the information that will help avoid issues when installing or removing assets."
				required={true}
			/>
			<SetupTextField
				label="Confirm available width (mm)"
				value={marketSetup.availableWidth}
				field="availableWidth"
				onConfirm={updateMarketSetup}
				required={true}
			/>
			<SetupTextField
				label="Confirm available height (mm)"
				value={marketSetup.availableHeight}
				field="availableHeight"
				onConfirm={updateMarketSetup}
				required={true}
			/>
			<SetupTextField
				label="Confirm available depth (mm)"
				value={marketSetup.availableDepth}
				field="availableDepth"
				onConfirm={updateMarketSetup}
				required={true}
			/>
			<SetupPickerField
				label="Client current vending setup"
				value={marketSetup.clientCurrentVendingSetup}
				onChange={value => updateMarketSetup({ clientCurrentVendingSetup: toNumber(value) })}
				options={Object.values(CLIENT_CURRENT_VENDING_SETUP_VALUE).map(value => ({
					label: CLIENT_CURRENT_VENDING_SETUP_VALUE_NAME[value],
					value,
				}))}
				hint="If none of these describe the scenario, ask Eamonn to add more options."
				required={true}
			/>

			<Text style={styles.sectionLabel}>Internet requirements</Text>
			<SetupPickerField
				label="Internet connectivity"
				value={marketSetup.internetConnectivity}
				onChange={value => updateMarketSetup({ internetConnectivity: toNumber(value) })}
				options={Object.values(INTERNET_CONNECTIVITY).map(value => ({
					label: INTERNET_CONNECTIVITY_NAME[value],
					value,
				}))}
				hint="Select the option that describes how internet connectivity will be supplied to the kiosk(s)."
				required={true}
			/>
			<SetupTextField
				label="Router model"
				value={marketSetup.routerModel}
				field="routerModel"
				onConfirm={updateMarketSetup}
				hint="Record the model of the modem or router installed at this market."
				required={requiresModem}
			/>
			<SetupTextField
				label="SIM number"
				value={marketSetup.simNumber}
				field="simNumber"
				onConfirm={updateMarketSetup}
				hint="Record the SIM number installed in the modem or router."
				required={requiresModem}
			/>

			<Text style={styles.sectionLabel}>Optional information</Text>
			<InformationCallout text="If any of the following are decided during the site visit, enter them here. Otherwise, leave the fields empty and complete them later." />
			<SetupPickerField
				label="Market brand"
				value={marketSetup.marketBrand}
				onChange={value => updateMarketSetup({ marketBrand: toNumber(value) })}
				options={Object.values(MACHINE_BRAND).map(value => ({
					label: MACHINE_BRAND_NAME[value],
					value,
				}))}
				required={true}
			/>
			<SetupPickerField
				label="Market type"
				value={marketSetup.marketType}
				onChange={value => updateMarketSetup({ marketType: toNumber(value) })}
				options={Object.values(MARKET_SETUP_MARKET_TYPE).map(value => ({
					label: MARKET_SETUP_MARKET_TYPE_NAME[value],
					value,
				}))}
				required={true}
			/>
			{
				isCustomBuildMarket && (
					<>
						<SetupPickerField
							label="Market finish"
							value={marketSetup.marketFinish}
							onChange={value => updateMarketSetup({ marketFinish: toNumber(value) })}
							options={Object.values(MARKET_SETUP_MARKET_FINISH_VALUE).map(value => ({
								label: MARKET_SETUP_MARKET_FINISH_VALUE_NAME[value],
								value,
							}))}
							required={true}
						/>
						{
							marketSetup.marketFinish === MARKET_SETUP_MARKET_FINISH_VALUE.OTHER && (
								<SetupTextField
									label="Custom finish"
									value={marketSetup.customFinish}
									field="customFinish"
									onConfirm={updateMarketSetup}
									hint="Describe the finish when 'Other' is selected."
									required={true}
								/>
							)
						}
					</>
				)
			}

			<Text style={styles.sectionLabel}>Market configuration - bays</Text>
			<InformationCallout text="If the required asset or bay size isn't available, ask Eamonn to add it." />
			<InformationCallout text="When selecting bays, select them from left to right." />
			<MarketSetupBays marketSetup={marketSetup} updateMarketSetup={updateMarketSetup} />

			<Text style={styles.sectionLabel}>Pre-deployment general steps</Text>
			<SetupSwitchField
				label="Inform client of relevant information"
				value={marketSetup.informClientOfRelevantInformation}
				onChange={value => updateMarketSetup({ informClientOfRelevantInformation: value })}
				required={true}
			/>
			<InformationCallout text="Inform the client of all relevant information (and check the above checkbox). A template email using information from this setup is provided." />
			<SetupSwitchField
				label="Test and tag power cords and electrical equipment"
				value={marketSetup.testAndTagElectricalEquipment}
				onChange={value => updateMarketSetup({ testAndTagElectricalEquipment: value })}
				hint="Must be completed by a licensed tester before deployment."
				required={true}
			/>
			<Text style={styles.sectionLabel}>Template email to send to client</Text>
			<SetupSwitchField
				label="Send email"
				value={marketSetup.sendEmail}
				onChange={value => updateMarketSetup({ sendEmail: value })}
				required={true}
			/>
			<Text style={styles.helperText}>Use the web app to open the pre-filled email template when required.</Text>

			<Text style={styles.sectionLabel}>Sentry steps</Text>
			<SetupSwitchField
				label="Set up Sentry planogram"
				value={marketSetup.setUpSentryPlanogram}
				onChange={value => updateMarketSetup({ setUpSentryPlanogram: value })}
				required={true}
			/>
			{!isAssignedMarketSetupWorker && (
				TICKET_FIELDS.map(config => {
					const created = Boolean(marketSetup?.[config.createdFlag]);
					return (
						<View key={config.key} style={styles.ticketField}>
							<Text style={styles.ticketLabel}>{config.label}</Text>
							<SetupTextField
								label="Ticket description"
								value={ticketDescriptions[config.key]}
								field={config.field}
								onConfirm={(values) => updateTicketDescription(config.key, values[config.field] || '')}
								multiline={true}
								disabled={created}
								hint={config.hint}
							/>
							<Button
								label={created ? 'Ticket created' : 'Generate ticket'}
								onPress={() => handleGenerateTicket(config)}
								disabled={created || !hasAssignedMarket}
								style={styles.ticketButton}
							/>
						</View>
					);
				})
			)}

			<Text style={styles.sectionLabel}>Fridge steps</Text>
			<SetupSwitchField
				label="Purchase or allocate fridges"
				value={marketSetup.purchaseOrAllocateFridges}
				onChange={value => updateMarketSetup({ purchaseOrAllocateFridges: value })}
				required={true}
			/>
			<SetupSwitchField
				label="Set up required fridges"
				value={marketSetup.setUpRequiredFridges}
				onChange={value => updateMarketSetup({ setUpRequiredFridges: value })}
				required={true}
			/>

			<Text style={styles.sectionLabel}>Food options</Text>
			<MarketSetupFoodOptions marketSetup={marketSetup} updateMarketSetup={updateMarketSetup} />

			<InformationCallout text="If the fridge needs gravity mats, order with PMG Engineering." />

			<Text style={styles.sectionLabel}>Shelving steps</Text>
			{
				isGondolaShelvingMarket && (
					<SetupSwitchField
						label="Purchase or allocate gondola shelving"
						value={marketSetup.purchaseOrAllocateGondolaShelving}
						onChange={value => updateMarketSetup({ purchaseOrAllocateGondolaShelving: value })}
						required={true}
					/>
				)
			}
			{
				isCustomBuildMarket && (
					<SetupSwitchField
						label="Confirm configuration"
						value={marketSetup.confirmConfiguration}
						onChange={value => updateMarketSetup({ confirmConfiguration: value })}
						required={true}
					/>
				)
			}
			{
				requiresMachineRemoval && (
					<>
						<Text style={styles.sectionLabel}>Vending machine steps</Text>
						<SetupSwitchField
							label="Book removal of vending machines"
							value={marketSetup.bookRemovalOfVendingMachines}
							onChange={value => updateMarketSetup({ bookRemovalOfVendingMachines: value })}
							required={true}
						/>
						<SetupSwitchField
							label="Organise emptying of vending machine"
							value={marketSetup.organiseEmptyingOfVendingMachine}
							onChange={value => updateMarketSetup({ organiseEmptyingOfVendingMachine: value })}
							required={true}
						/>
					</>
				)
			}

			<Text style={styles.sectionLabel}>Internet connectivity steps</Text>
			{
				requiresModem && (
					<SetupSwitchField
						label="Purchase or allocate modem to supply internet connectivity"
						value={marketSetup.purchaseOrAllocateModem}
						onChange={value => updateMarketSetup({ purchaseOrAllocateModem: value })}
						required={true}
					/>
				)
			}
			<SetupSwitchField
				label="Test for internet connectivity"
				value={marketSetup.testInternetConnectivity}
				onChange={value => updateMarketSetup({ testInternetConnectivity: value })}
				hint="Test for connectivity at the location using 365 retail markets custom Windows application."
				required={true}
			/>

			{!isAssignedMarketSetupWorker && (
				<>
					<Text style={styles.sectionLabel}>Campaigns and promotions</Text>
					<Text style={styles.helperText}>Select one or more campaigns that will run at the market. The campaigns will commence when the market setup in Sentry is complete. If a campaign is deleted after it has been selected here and before the setup has been completed, it will not be applied.</Text>
					<SetupMultiSelector
						items={marketSetup.initialCampaigns || []}
						onChange={ids => updateMarketSetup({ initialCampaigns: flattenObjectDataInArray(ids) })}
						SelectorComponent={CampaignSelector}
						placeholder="Add campaign"
						selectorProps={{ buttonStyle: styles.selectorButton }}
					/>
				</>
			)}

			<Text style={styles.sectionLabel}>Final steps</Text>
			<SetupSwitchField
				label="Organise installation of micro market"
				value={marketSetup.organiseInstallationOfMM}
				onChange={value => updateMarketSetup({ organiseInstallationOfMM: value })}
				required={true}
			/>
			<SetupPhotoSection
				title="Final photos"
				photos={marketSetup.finalPhotos || []}
				onAddPhoto={(link) => addPhoto({ link, targetField: 'finalPhotos' })}
				onUpdateComment={handleUpdateMediaComment}
				addButtonLabel="Add final photo"
			/>
		</SetupSection>
	);
};

export default MarketSetupMarketSection;
